import { css, cx } from 'emotion';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBackgrounds } from '../hooks/useBackgrounds';
import { BookContext } from './Book';
import { Page } from './Page/Page';
import { ReactComponent as ArrowIcon } from '../assets/book-arrow.svg';
import { ReactComponent as CloseIcon } from '../assets/close.svg';

import { PageContent } from './PageContent/PageContent';
import { IBookProps } from '../interfaces/IBookProps';
import { I18N_TOKEN } from '../services/InjectTokens';
import { useInject } from 'dependable-react';

const bookWrapper = (pageStyles: any, isZoomed = false) => css`
  width: ${pageStyles.width * 2}mm;
  height: ${pageStyles.height}mm;
  position: ${isZoomed ? 'absolute' : 'relative'};
  transform: scale(${isZoomed ? '0.6' : '0.45'});

  margin-bottom: 20px;
`;
const bookStyle = css`
  perspective: 6000px;
  transform-style: preserve-3d;
  position: absolute;

  width: 100%;
  height: 100%;
  top: 0;
  transition: all 0.8s ease;
`;

const bookActionStyle = (isActive = false) => css`
  display: flex;
  flex-direction: ${isActive ? 'row-reverse' : 'row'};
  align-items: center;

  border: none;
  background: transparent;
  color: #72659f;

  position: absolute;
  bottom: 0;
  left: ${isActive ? '50%' : '52.5%'};

  transform: translateX(${isActive ? '-50%' : '0'});

  font-size: 18px;
  font-weight: bold;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;

  transition: all 0.6s ease;

  &:active,
  &:focus {
    outline: none;
  }

  svg {
    width: 48px;
    height: 48px;
    padding: 0 5px;
    transform: rotateY(${isActive ? '190deg' : '0'}) translateY(-3px);
    transition: all 0.5s ease;
  }
`;

const zoomWrapper = (isZoomed = false) => css`
  position: ${isZoomed ? 'fixed' : 'static'};
  width: ${isZoomed ? '100%' : 'auto'};
  height: ${isZoomed ? '100%' : 'auto'};
  top: 0;
  left: 0;
  background-color: ${isZoomed ? 'rgba(0, 0, 0, 0.7)' : 'transparent'};
  z-index: 9998;
  display: grid;
  place-items: center;

  .exit {
    position: fixed;
    z-index: 9999;
    right: 20px;
    top: 20px;
    width: 36px;
    height: 36px;
    cursor: pointer;
    color: #ffffff;
  }
`;

export const Desktop = (props: IBookProps) => {
  const i18n = useInject(I18N_TOKEN);
  const [activePage, setActivePage] = useState(
    (props.page ? (Number(props.page) % 2 === 0 ? Number(props.page) + 2 : Number(props.page) + 3) : null) || 0,
  );
  const [animating, setAnimating] = useState<any>(null);
  const [oldActive, setOldActive] = useState<any>(null);
  const [direction, setDirection] = useState<'forwards' | 'backwards'>('forwards');
  const backgrounds = useBackgrounds(props.pages);
  const ctx = useContext<any>(BookContext);

  const handleButtonClick = useCallback(() => {
    if (activePage > 0) {
      for (let index = activePage; index >= 0; index--) {
        const timeout = setTimeout(() => {
          setActivePage(index);
          clearTimeout(timeout);
        }, 300);
      }
    } else {
      setActivePage(2);
    }
  }, [activePage, setActivePage]);

  const leftPosition = useMemo(() => {
    if (activePage === 0) {
      return '-25%';
    } else if (activePage === backgrounds.length) {
      return '25%';
    } else {
      return '0';
    }
  }, [activePage]);

  const handlePageClick = (index: number) => {
    return () => {
      if (index === activePage) {
        setDirection('forwards');
        setOldActive(index - 1);
        setActivePage(index + 2);
        setAnimating(index + 2);
      } else if (index < activePage) {
        const oldPage = activePage;
        setDirection('backwards');
        setActivePage(oldPage - 2);
        setOldActive(oldPage);
        setAnimating(oldPage - 2);
      }
    };
  };

  const handleTransitionEnd = () => {
    setAnimating(null);
    setOldActive(null);
  };

  const calculateClassNames = useCallback(
    (index: number) => {
      let classNames = [];
      if (direction === 'forwards') {
        classNames = [
          activePage === index || activePage === index + 1 ? 'is-active' : '',
          animating === index || animating === index + 1 ? 'is-animating' : '',
          oldActive === index || oldActive === index - 1 ? 'was-active' : '',
          oldActive === index || oldActive === index - 1 ? 'is-animating' : '',
        ];
      } else {
        classNames = [
          activePage === index || activePage === index - 1 ? 'is-active' : '',
          animating === index || animating === index - 1 ? 'is-animating' : '',
          oldActive === index || oldActive === index - 1 ? 'was-active' : '',
          oldActive === index || oldActive === index - 1 ? 'is-animating' : '',
        ];
      }

      if (index === activePage || (activePage >= index - 4 && activePage <= index + 4)) {
        classNames.push('is-visible');
      }

      return classNames;
    },
    [activePage, direction, animating, oldActive],
  );

  useEffect(() => {
    if (props.page !== 'undefined' && props.page !== null) {
      const page = Number(props.page) % 2 === 0 ? Number(props.page) + 2 : Number(props.page) + 3;
      if (props.page === 0) {
        setActivePage(0);
      } else {
        setActivePage(page);
      }
      props.resetCurrentPage();
    }
  }, [props.page]);

  return (
    <Fragment>
      <div className={zoomWrapper(props.isZoomed)}>
        {props.isZoomed && <CloseIcon className="exit" onClick={props.handleZoom} />}
        <div className={bookWrapper(ctx.pageDimensions, props.isZoomed)}>
          <div
            className={bookStyle}
            style={{
              left: leftPosition,
            }}
          >
            {backgrounds.map((page: any, index: number) => {
              return (
                <Page
                  key={index}
                  page={index}
                  isEmptyPage={!page.background}
                  id={`page-${index}-content`}
                  isCoverFront={page.isCoverFront}
                  isCoverBack={page.isCoverBack}
                  coverDimensions={ctx.coverDimensions}
                  className={cx(['page', ...calculateClassNames(index)])}
                  style={{
                    background: !page.background && '#e1e1e1',
                  }}
                  onClick={handlePageClick(index)}
                  onTransitionEnd={handleTransitionEnd}
                >
                  <PageContent
                    page={page.isCoverBack ? 0 : page.contentId} // back cover uses content from page 0 (front cover)
                    isCoverFront={page.isCoverFront}
                    isCoverBack={page.isCoverBack}
                    backgroundImage={page.background}
                  />
                </Page>
              );
            })}
          </div>
        </div>
      </div>
      {backgrounds.length > 1 && (
        <button className={bookActionStyle(activePage > 0)} onClick={handleButtonClick}>
          {activePage > 0 ? i18n.t('list_back') : i18n.t('list_forward')}
          <ArrowIcon />
        </button>
      )}
    </Fragment>
  );
};
